import { useCallback, useMemo } from 'react';

import mixpanelBrowser from 'mixpanel-browser';

import { getEnvVars } from '@order/utils';

import { mixpanelLogger as logger } from './Mixpanel.logger';

import type { Mixpanel } from './Mixpanel.types';

export const useMixpanel = (): Mixpanel => {
  const { MIXPANEL_PROJECT_TOKEN } = getEnvVars();

  const isDisabled = !MIXPANEL_PROJECT_TOKEN;

  // ─── Handlers ────────────────────────────────────────────────────────────

  const initialize: Mixpanel['initialize'] = useCallback(
    async (params = defaultInitializeParams) => {
      const { isFeatureFlagsEnabled } = params;

      if (isDisabled) {
        logger.info('skip initialize()');

        return;
      }

      logger.info('initialize()');

      return new Promise<void>((resolve) => {
        mixpanelBrowser.init(MIXPANEL_PROJECT_TOKEN, {
          flags: isFeatureFlagsEnabled,
          loaded() {
            resolve();
          },
        });
      });
    },
    [MIXPANEL_PROJECT_TOKEN, isDisabled],
  );

  const identify: Mixpanel['identify'] = useCallback(
    async (userId) => {
      if (isDisabled) {
        logger.info(`skip identify(${userId})`);

        return;
      }

      logger.info(`identify(${userId})`);

      try {
        await mixpanelBrowser.identify(userId);
      } catch {}
    },
    [isDisabled],
  );

  const reset: Mixpanel['reset'] = useCallback(() => {
    if (isDisabled) {
      logger.info('skip reset()');

      return;
    }

    logger.info('reset()');

    try {
      mixpanelBrowser.reset();
    } catch {}
  }, [isDisabled]);

  const setSuperProperty: Mixpanel['setSuperProperty'] = useCallback(
    (key, value) => {
      if (isDisabled) {
        logger.info(`skip setSuperProperty(${key}, ${JSON.stringify(value)})`);

        return;
      }

      logger.info(`setSuperProperty(${key}, ${JSON.stringify(value)})`);

      try {
        mixpanelBrowser.register({ [key]: value });
      } catch {}
    },
    [isDisabled],
  );

  const setUserProperty: Mixpanel['setUserProperty'] = useCallback(
    (key, value) => {
      if (isDisabled) {
        logger.info(`skip setUserProperty(${key}, ${JSON.stringify(value)})`);

        return;
      }

      logger.info(`setUserProperty(${key}, ${JSON.stringify(value)})`);

      try {
        mixpanelBrowser.people.set(key, value);
      } catch {}
    },
    [isDisabled],
  );

  const track: Mixpanel['track'] = useCallback(
    (eventName, properties) => {
      if (isDisabled) {
        logger.info(`skip track(${eventName}, ...)`);

        return;
      }

      logger.info(`track(${eventName}, ...)`);

      try {
        mixpanelBrowser.track(eventName, properties);
      } catch {}
    },
    [isDisabled],
  );

  const getFeatureFlagValue: Mixpanel['getFeatureFlagValue'] = useCallback(
    async (featureFlagKey, fallbackValue) => {
      if (isDisabled) {
        logger.info(`skip getFeatureFlagValue(${featureFlagKey}, ...)`);

        return fallbackValue;
      }

      logger.info(`getFeatureFlagValue(${featureFlagKey}, ...)`);

      try {
        return await mixpanelBrowser.flags.get_variant_value(featureFlagKey, fallbackValue);
      } catch {
        return fallbackValue;
      }
    },
    [isDisabled],
  );

  const getAllFeatureFlagVariants: Mixpanel['getAllFeatureFlagVariants'] = useCallback(async () => {
    if (isDisabled) {
      logger.info('skip getAllFeatureFlagVariants()');

      return new Map();
    }

    logger.info('getAllFeatureFlagVariants()');

    try {
      // Mixpanel SDK's `reset` method clears the flag fetch state before its anonymous reload starts.
      // We explicitly load here so get_all_variants() does not return an empty map during that gap.
      await mixpanelBrowser.flags.load_flags();

      return await mixpanelBrowser.flags.get_all_variants();
    } catch {
      return new Map();
    }
  }, [isDisabled]);

  // ─────────────────────────────────────────────────────────────────────────

  return useMemo(
    () => ({
      getAllFeatureFlagVariants,
      getFeatureFlagValue,
      initialize,
      identify,
      reset,
      setSuperProperty,
      setUserProperty,
      track,
    }),
    [
      getAllFeatureFlagVariants,
      getFeatureFlagValue,
      initialize,
      identify,
      reset,
      setSuperProperty,
      setUserProperty,
      track,
    ],
  );
};

const defaultInitializeParams = { isFeatureFlagsEnabled: false };
